<script setup lang="ts">
import { Payment } from '@commercetools/platform-sdk';
import { cents } from '@nuts/auto-delivery-sdk/dist/utils/money';
import { computed } from 'vue';

import AppliedDiscountsPopper from '@/components/base/AppliedDiscountsPopper.vue';
import CreditCardLogo from '@/components/base/assets/CreditCardLogo.vue';
import InfoIcon from '@/components/base/assets/InfoIcon.vue';
import { PaymentItem } from '@/composables/usePayment';
import money from '@/filters/money';
import { adaptSavings } from '@/utils/cart';
import { CartSavingsSummary } from '@/utils/money';

const props = defineProps<{
  paymentItems: PaymentItem[];
  payments: Payment[];
  totalSavings?: CartSavingsSummary;
}>();

const savingsBreakdown = computed(() =>
  props.totalSavings ? adaptSavings(props.totalSavings) : [],
);
const formatAmount = ({ label, amount }: PaymentItem) => {
  if (!amount) {
    return 'TBD';
  }
  if (cents(amount) === 0 && (label === 'Shipping' || label === 'Heat-Resistant Packaging')) {
    return 'FREE';
  }
  return money(amount);
};

const describeCreditCard = (payment: Payment) => {
  const { cardType, lastFour } = payment.custom?.fields ?? {};
  return `${cardType} ending in ${lastFour}`.replace('American Express ending in', cardType);
};
const applePayLogo = nutshell['img/apple-pay-logo.svg'];
const paypalLogo = nutshell['img/paypal-logo.svg'];
const shopPayLogo = nutshell['img/shop-pay-logo.svg'];
</script>

<template>
  <div class="text-sm leading-6">
    <div
      v-for="item in paymentItems"
      :key="item.label"
      class="flex justify-between gap-4"
      :class="{ 'font-bold': item.label === 'Subtotal' }"
    >
      <div class="flex align-center">
        {{ item.detail || item.label }}
      </div>

      <div>{{ formatAmount(item) }}</div>
    </div>
    <div class="pt-5 pb-4 mt-4 border-t-2 border-b-2 border-solid border-black">
      <div v-if="!payments.length" class="flex justify-between text-base font-bold">
        <div>Total</div>
        <div>$0.00</div>
      </div>
      <div
        v-else
        v-for="payment in payments"
        :key="payment.id"
        class="flex justify-between text-base font-bold"
      >
        <div
          v-if="
            payment.paymentMethodInfo.method === 'credit_card' && payment.custom?.fields.cardType
          "
        >
          {{ describeCreditCard(payment) }}
          <CreditCardLogo class="w-8 ml-1" :cardType="payment.custom.fields.cardType" />
        </div>
        <div v-else-if="payment.paymentMethodInfo.method === 'paypal_account' && payment.custom">
          <div>
            Paid with PayPal <img class="w-16 ml-1 md:w-20" :src="paypalLogo" alt="paypal logo" />
            <div class="text-sm">{{ payment.custom.fields.payerEmail }}</div>
          </div>
        </div>
        <div v-else-if="payment.paymentMethodInfo.method === 'apple_pay_account' && payment.custom">
          <div>
            Paid with Apple Pay
            <img class="w-16 mb-1 ml-1 md:w-10" :src="applePayLogo" alt="apple pay logo" />
            <div class="text-sm">{{ payment.custom.fields.payerEmail }}</div>
          </div>
        </div>
        <div v-else-if="payment.paymentMethodInfo.method === 'credit_account'">
          Charged to Credit Account
        </div>
        <div v-else-if="payment.paymentMethodInfo.method === 'offline'">Offline Payment</div>
        <div v-else-if="payment.paymentMethodInfo.method === 'us_bank_account'">
          US Bank Account ending in {{ payment.custom?.fields.lastFour }}
        </div>
        <div v-else-if="payment.paymentMethodInfo.method === 'shop_pay' && payment.custom">
          <div>
            Paid with Shop Pay
            <img class="w-16 ml-1 md:w-20" :src="shopPayLogo" alt="Shop Pay logo" />
            <div class="text-sm">{{ payment.custom.fields.shopPayEmail }}</div>
          </div>
        </div>
        <div v-else>Total</div>
        <div>{{ money(payment.amountPlanned) }}</div>
      </div>
      <div v-if="totalSavings" class="flex justify-between gap-4 mt-1">
        <div class="flex align-center">
          Total Savings
          <AppliedDiscountsPopper :discounts="savingsBreakdown">
            <InfoIcon alt="Total Savings Info Icon" :size="16" />
          </AppliedDiscountsPopper>
        </div>
        <div>{{ money(totalSavings.value) }}</div>
      </div>
    </div>
  </div>
</template>
